import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { EMPTY, filter, fromEvent, switchMap, timer } from 'rxjs';

/** Snooze ends, as epoch ms. Expired entries are pruned on every change. */
interface IncidentToastSnoozes {
  allUntil?: number;
  incidents: Record<string, number>;
}

export type IncidentToastSnoozeDuration = '15m' | '1h' | '4h' | 'tomorrow';

const STORAGE_KEY = 'incidite.incidentToastSnooze';

/** "Until tomorrow" ends at the next local 08:00. */
const MORNING_HOUR = 8;

const EMPTY_SNOOZES: IncidentToastSnoozes = { incidents: {} };

/**
 * Snoozed realtime incident toasts — all of them, or per incident — for this
 * browser. Kept in localStorage directly rather than in a persisted NGXS
 * state: the storage plugin only reads on startup, while a snooze set in one
 * tab must silence every open tab right away (`storage` events).
 */
@Injectable({ providedIn: 'root' })
export class IncidentToastSnoozeService {
  private destroyRef = inject(DestroyRef);
  private window = inject(DOCUMENT).defaultView;

  private readonly snoozes = signal<IncidentToastSnoozes>(this.read());

  /** Until when all incident toasts are snoozed, if they are. */
  readonly allSnoozedUntil = computed(() => {
    const { allUntil } = this.snoozes();
    return allUntil ? new Date(allUntil) : undefined;
  });

  /** How many incidents are snoozed individually. */
  readonly snoozedIncidentCount = computed(
    () => Object.keys(this.snoozes().incidents).length
  );

  constructor() {
    this.initCrossTabSync();
    this.initExpiry();
  }

  isSnoozed(incidentId: number): boolean {
    const { allUntil, incidents } = this.snoozes();
    const now = Date.now();
    return (allUntil ?? 0) > now || (incidents[incidentId] ?? 0) > now;
  }

  snoozeAll(duration: IncidentToastSnoozeDuration): void {
    this.write({ ...this.snoozes(), allUntil: snoozeEnd(duration) });
  }

  snoozeIncident(
    incidentId: number,
    duration: IncidentToastSnoozeDuration
  ): void {
    const current = this.snoozes();
    this.write({
      ...current,
      incidents: { ...current.incidents, [incidentId]: snoozeEnd(duration) },
    });
  }

  /** Ends every snooze. */
  resume(): void {
    this.write(EMPTY_SNOOZES);
  }

  private initCrossTabSync() {
    if (!this.window) {
      return;
    }
    fromEvent<StorageEvent>(this.window, 'storage')
      .pipe(
        filter(event => event.key === STORAGE_KEY),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.snoozes.set(this.read()));
  }

  /** Re-prunes at the earliest snooze end, so indicators clear on time. */
  private initExpiry() {
    toObservable(this.snoozes)
      .pipe(
        switchMap(snoozes => {
          const ends = [
            snoozes.allUntil,
            ...Object.values(snoozes.incidents),
          ].filter((end): end is number => end !== undefined);
          return ends.length
            ? timer(Math.max(0, Math.min(...ends) - Date.now()))
            : EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.snoozes.set(prune(this.snoozes())));
  }

  private write(snoozes: IncidentToastSnoozes) {
    const pruned = prune(snoozes);
    this.snoozes.set(pruned);
    try {
      this.window?.localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
    } catch {
      // storage unavailable (private mode, quota): the snooze holds for this tab
    }
  }

  private read(): IncidentToastSnoozes {
    try {
      const raw = this.window?.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as IncidentToastSnoozes) : null;
      return prune({
        allUntil: parsed?.allUntil,
        incidents: parsed?.incidents ?? {},
      });
    } catch {
      return EMPTY_SNOOZES;
    }
  }
}

function prune(snoozes: IncidentToastSnoozes): IncidentToastSnoozes {
  const now = Date.now();
  return {
    allUntil:
      snoozes.allUntil && snoozes.allUntil > now ? snoozes.allUntil : undefined,
    incidents: Object.fromEntries(
      Object.entries(snoozes.incidents).filter(([, end]) => end > now)
    ),
  };
}

function snoozeEnd(duration: IncidentToastSnoozeDuration): number {
  const now = new Date();
  switch (duration) {
    case '15m':
      return now.getTime() + 15 * 60_000;
    case '1h':
      return now.getTime() + 60 * 60_000;
    case '4h':
      return now.getTime() + 4 * 60 * 60_000;
    case 'tomorrow': {
      const morning = new Date(now);
      morning.setHours(MORNING_HOUR, 0, 0, 0);
      if (morning <= now) {
        morning.setDate(morning.getDate() + 1);
      }
      return morning.getTime();
    }
  }
}
